import classNames from "classnames";
import { useGrowl } from "PFApp/use_growl";
import SectionBox from "PFComponents/box/section_box";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getThresholds } from "../services/api";
import css from "./booking_thresholds.module.scss";
import EditThresholdsModal from "./parts/edit_thresholds_modal";
import Section from "./parts/section";
import { IDataItem } from "./types";

enum KIND {
  utilization = "utilization",
  overbooking = "overbooking",
  heatmap = "heatmap"
}

export const addExtraInfo = (collection, kind) =>
  collection.map((item, index) => ({ ...item, key: `${kind}-${index}` }));

const BookingThresholds = ({ onConfigUpdate }) => {
  const { t } = useTranslation(["bookingModule", "translation"]);
  const growl = useGrowl();

  const [loading, setLoading] = useState(true);

  const [colorsModalItem, setColorModalItem] = useState<IDataItem | null>(null);

  const [utilizationData, setUtilizationData] = useState<IDataItem[]>([]);
  const [overbookingData, setOverbookingData] = useState<IDataItem[]>([]);
  const [heatmapData, setHeatmapData] = useState<IDataItem[]>([]);

  const [editedColorItem, setEditedColorItem] = useState(null);

  const [editedRowKey, setEditedRowKey] = useState(null);

  useEffect(() => {
    const fetchData = async (kind) => await getThresholds(kind);

    Promise.all(Object.values(KIND).map((item) => fetchData(KIND[item])))
      .then((res) => {
        const utilization = res.filter((item) => item.entries?.[0]?.kind === KIND.utilization)?.[0]?.entries;
        const overbooking = res.filter((item) => item.entries?.[0]?.kind === KIND.overbooking)?.[0]?.entries;
        const heatmap = res.filter((item) => item.entries?.[0]?.kind === KIND.heatmap)?.[0]?.entries;

        setUtilizationData(utilization ? addExtraInfo(utilization, KIND.utilization) : []);
        setOverbookingData(overbooking ? addExtraInfo(overbooking, KIND.overbooking) : []);
        setHeatmapData(heatmap ? addExtraInfo(heatmap, KIND.heatmap) : []);
        setLoading(false);
      })
      .catch((err) =>
        growl({
          message: err.responseJSON.errors?.[0].detail || t("translation:somethingWentWrong"),
          kind: "error"
        })
      );
  }, []);

  return (
    <div>
      <SectionBox className={classNames(css.thresholdContainer)}>
        <Section
          kind={KIND.utilization}
          title={t("bookingModule:config.thresholds.utilizationWarning")}
          data={utilizationData}
          setOpenColorsModal={setColorModalItem}
          setData={setUtilizationData}
          loading={loading}
          editedRowKey={editedRowKey}
          setEditedRowKey={setEditedRowKey}
          editedColorItem={editedColorItem}
          setEditedColorItem={setEditedColorItem}
          onConfigUpdate={onConfigUpdate}
        />
        <Section
          kind={KIND.overbooking}
          title={t("bookingModule:config.thresholds.overbookingWarning")}
          data={overbookingData}
          setOpenColorsModal={setColorModalItem}
          setData={setOverbookingData}
          loading={loading}
          editedRowKey={editedRowKey}
          setEditedRowKey={setEditedRowKey}
          editedColorItem={editedColorItem}
          setEditedColorItem={setEditedColorItem}
          onConfigUpdate={onConfigUpdate}
        />
        <Section
          kind={KIND.heatmap}
          title={t("bookingModule:config.thresholds.heatmapColors")}
          data={heatmapData}
          setOpenColorsModal={setColorModalItem}
          setData={setHeatmapData}
          loading={loading}
          editedRowKey={editedRowKey}
          setEditedRowKey={setEditedRowKey}
          editedColorItem={editedColorItem}
          setEditedColorItem={setEditedColorItem}
          onConfigUpdate={onConfigUpdate}
        />
      </SectionBox>
      {colorsModalItem && (
        <EditThresholdsModal
          selectedItem={colorsModalItem}
          onClose={() => setColorModalItem(null)}
          setEditedColor={setEditedColorItem}
        />
      )}
    </div>
  );
};

export default BookingThresholds;
